import React from 'react';
import { FaTimes } from 'react-icons/fa';

const DetailsModal = ({ data, isOpen, onClose }) => {
  if (!isOpen || !data) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours24 = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${day} ${month} ${year}, ${hours12}:${minutes} ${ampm}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const slotInfo = data.slot_object && data.slot_object.length > 0 
    ? data.slot_object[0] 
    : null;

  // Determine if this is an appointment or patient based on data structure
  const isAppointment = !!data.appointment_status;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{data.profile?.name || 'N/A'}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {isAppointment ? `Sukra ID: ${data.sukra_id || 'N/A'}` : `Patient ID: ${data.sukra_id || 'N/A'}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Top Row: Patient & Appointment/Details Info */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {/* Patient Information */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">Patient Details</h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-600">Age / Gender</p>
                  <p className="font-medium text-gray-900">{data.profile?.age || '-'} / {data.profile?.gender?.charAt(0)?.toUpperCase() || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900 text-sm">{data.user?.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Email</p>
                  <p className="font-medium text-gray-900 text-sm">{data.user?.email || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Appointment/Details Information */}
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">
                {isAppointment ? 'Appointment Info' : 'Details'}
              </h3>
              <div className="space-y-2">
                {isAppointment && (
                  <>
                    <div>
                      <p className="text-xs text-gray-600">Booked Date & Time</p>
                      <p className="font-medium text-gray-900 text-sm">{formatDate(data.slot_start_time)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Created At</p>
                      <p className="font-medium text-gray-900 text-sm">{formatDate(data.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Status</p>
                      <p className="font-medium text-gray-900 capitalize text-sm">{data.appointment_status || 'N/A'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Facility Information */}
            {data.facility && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Facility Details</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-600">Branch</p>
                    <p className="font-medium text-gray-900 text-sm">
                      {data.facility?.branch_name 
                        ? data.facility.branch_name.replace('VITAL INSIGHTS-', '')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">City / State</p>
                    <p className="font-medium text-gray-900 text-sm">{data.facility?.city_name || 'N/A'} / {data.facility?.state || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Facility Code</p>
                    <p className="font-medium text-gray-900 text-sm">{data.facility?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Slot & Payment Row */}
          {isAppointment && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Slot Information */}
              {slotInfo && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">Slot Information</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-600">Slot Date</p>
                      <p className="font-medium text-gray-900 text-sm">{slotInfo.slotDate || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Slot Time</p>
                      <p className="font-medium text-gray-900 text-sm">{slotInfo.slotTime || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Information */}
              <div className={`rounded-lg p-4 border ${data.payment_id && data.payment_id.trim() !== '' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Payment Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-600">{data.payment_id && data.payment_id.trim() !== '' ? 'Amount Paid' : 'Amount Due'}</p>
                    <p className={`font-bold text-lg ${data.payment_id && data.payment_id.trim() !== '' ? 'text-green-600' : 'text-red-600'}`}>₹{data.amount || '0'}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-600">Discount</p>
                    <p className="font-medium text-gray-900">₹{data.discount_amount || '0'}</p>
                  </div>
                  <div className={`pt-2 border-t ${data.payment_id && data.payment_id.trim() !== '' ? 'border-green-200' : 'border-red-200'}`}>
                    <p className="text-xs text-gray-600">Status</p>
                    <p className={`font-semibold text-sm ${data.payment_id && data.payment_id.trim() !== '' ? 'text-green-600' : 'text-red-600'}`}>
                      {data.payment_id && data.payment_id.trim() !== '' ? 'PAID' : 'UNPAID'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Test Details */}
          {data.appointmentTests && data.appointmentTests.length > 0 && (
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">Test Details</h3>
              <div className="grid grid-cols-3 gap-3">
                {data.appointmentTests.map((test, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-indigo-100">
                    <p className="text-xs text-gray-600 mb-1">Test Name</p>
                    <p className="font-medium text-gray-900 text-sm">{test.availableTest?.name || 'N/A'}</p>
                    <p className="text-xs text-gray-500 mt-2">Code: {test.availableTest?.test_code || 'N/A'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {data.appointment_notes && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Notes</h3>
              <p className="text-sm text-gray-700">{data.appointment_notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailsModal;
